import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
	Unique,
} from 'typeorm';
import { BaseEntity } from './base.entity';
import { ClientAddressEntity } from './client-address.entity';
import type { ClientLicenseMatchLogEntity } from './client-license-match-log.entity';
import { ClientLicenseEntity } from './client-license.entity';
import { ClientEntity } from './client.entity';

export type ClientLicenseMatchStatus =
	| 'pending_review'
	| 'confirmed'
	| 'rejected';

@Index('idx_clm_harvest', ['harvestedAt'])
@Index('idx_clm_client', ['fkClientId'])
@Index('idx_clm_status', ['status'])
@Index('idx_clm_state', ['registryStateCode'])
@Unique('uq_clm_client_registry', ['fkClientId', 'registryLicenseId'])
@Entity('client_license_match')
export class ClientLicenseMatchEntity extends BaseEntity {
	@Column('int', { name: 'fk_client__id', unsigned: true })
	public fkClientId!: number;

	@Column('int', {
		name: 'fk_client_address__id',
		nullable: true,
		unsigned: true,
	})
	public fkClientAddressId!: number | null;

	@Column('int', {
		name: 'fk_client_license__id',
		nullable: true,
		unsigned: true,
	})
	public fkClientLicenseId!: number | null;

	@Column('int', { name: 'registry_license_id', unsigned: true })
	public registryLicenseId!: number;

	@Column('char', { name: 'registry_state_code', length: 2 })
	public registryStateCode!: string;

	@Column('varchar', { name: 'registry_license_number', length: 100 })
	public registryLicenseNumber!: string;

	@Column('varchar', { name: 'registry_license_type', length: 100 })
	public registryLicenseType!: string;

	@Column('varchar', {
		name: 'registry_entity_type',
		nullable: true,
		length: 50,
	})
	public registryEntityType!: string | null;

	/**
	 * Denormalized registrant display name from the matched registry row
	 * (PD-13891). Populated at matchmaker write-time as
	 * COALESCE(business_name, entity_name) so facility records whose name
	 * lives in `entity_name` rather than `business_name` still render a name.
	 * Lets the Pending Matches list + per-client review wizard display,
	 * search, sort, and filter on the registrant name WITHOUT a cross-DB join
	 * into `workd_mission_control.lm_registry` (which TypeORM cannot hydrate).
	 */
	@Column('varchar', {
		name: 'registry_business_name',
		nullable: true,
		length: 255,
	})
	public registryBusinessName!: string | null;

	/**
	 * Denormalized "doing business as" alias from the matched registry row
	 * (PD-13891). A distinct value from the business name, shown alongside it.
	 */
	@Column('varchar', {
		name: 'registry_dba_name',
		nullable: true,
		length: 255,
	})
	public registryDbaName!: string | null;

	/**
	 * Denormalized normalized source classification from the matched registry
	 * row (`lm_registry.source_type`: STATE_BOARD / NPI / DEA /
	 * CONTROLLED_SUBSTANCE / TOBACCO / LIQUOR / KYC / OTHER), PD-13891. Powers
	 * the Pending Matches "Type" filter (State Board / DEA / …) locally, without
	 * a cross-DB join.
	 */
	@Column('varchar', {
		name: 'registry_source_type',
		nullable: true,
		length: 50,
	})
	public registrySourceType!: string | null;

	@Column('int', { name: 'match_pass' })
	public matchPass!: number;

	@Column('varchar', { name: 'match_method', length: 50 })
	public matchMethod!: string;

	@Column('decimal', {
		name: 'confidence_score',
		precision: 5,
		scale: 2,
	})
	public confidenceScore!: string;

	@Column('json', { name: 'match_signals', nullable: true })
	public matchSignals!: Record<string, unknown> | null;

	@Column('enum', {
		name: 'status',
		enum: ['pending_review', 'confirmed', 'rejected'],
		default: 'pending_review',
	})
	public status!: ClientLicenseMatchStatus;

	@Column('int', {
		name: 'reviewed_by_user_id',
		nullable: true,
		unsigned: true,
	})
	public reviewedByUserId!: number | null;

	@Column('datetime', { name: 'reviewed_at', nullable: true })
	public reviewedAt!: Date | null;

	@Column('varchar', {
		name: 'rejection_reason',
		nullable: true,
		length: 500,
	})
	public rejectionReason!: string | null;

	@Column('datetime', { name: 'harvested_at', nullable: true })
	public harvestedAt!: Date | null;

	@ManyToOne(() => ClientEntity, { onDelete: 'CASCADE', onUpdate: 'RESTRICT' })
	@JoinColumn([{ name: 'fk_client__id', referencedColumnName: 'id' }])
	public fkClient!: ClientEntity;

	@ManyToOne(() => ClientAddressEntity, {
		onDelete: 'SET NULL',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_client_address__id', referencedColumnName: 'id' }])
	public fkClientAddress!: ClientAddressEntity | null;

	@ManyToOne(() => ClientLicenseEntity, {
		onDelete: 'SET NULL',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_client_license__id', referencedColumnName: 'id' }])
	public fkClientLicense!: ClientLicenseEntity | null;

	@OneToMany('ClientLicenseMatchLogEntity', 'fkClientLicenseMatch')
	public logs!: Array<ClientLicenseMatchLogEntity>;
}
